import React, { createContext, useContext, useEffect, useState } from 'react';
import { onlineManager, useQueryClient } from '@tanstack/react-query';

interface OnlineStatusContextType {
  /** Whether React Query currently considers the app online. */
  isOnline: boolean;
  /**
   * Number of mutations that ran offline and are paused, waiting to sync once
   * connectivity returns. Drives the "N changes pending" UI.
   */
  pendingMutationCount: number;
}

const OnlineStatusContext = createContext<OnlineStatusContextType | undefined>(undefined);

/**
 * Exposes the app's online/offline state and the offline write-queue depth to
 * the UI. Both values are derived from React Query itself (`onlineManager` and
 * the mutation cache), so the banner can never disagree with the engine that
 * actually pauses/resumes mutations.
 *
 * Must be mounted inside the QueryClientProvider / PersistQueryClientProvider so
 * `useQueryClient` resolves.
 */
export const OnlineStatusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const [isOnline, setIsOnline] = useState(() => onlineManager.isOnline());
  const [pendingMutationCount, setPendingMutationCount] = useState(0);

  // Mirror onlineManager's state. Reading isOnline() inside the listener keeps
  // us correct regardless of how the listener is invoked across versions.
  useEffect(
    () => onlineManager.subscribe(() => setIsOnline(onlineManager.isOnline())),
    [],
  );

  // Track how many mutations are paused (offline writes awaiting replay).
  useEffect(() => {
    const mutationCache = queryClient.getMutationCache();
    const recount = () => {
      const paused = mutationCache.getAll().filter((m) => m.state.isPaused).length;
      setPendingMutationCount(paused);
    };
    recount();
    return mutationCache.subscribe(recount);
  }, [queryClient]);

  return (
    <OnlineStatusContext.Provider value={{ isOnline, pendingMutationCount }}>
      {children}
    </OnlineStatusContext.Provider>
  );
};

export const useOnlineStatus = (): OnlineStatusContextType => {
  const context = useContext(OnlineStatusContext);
  if (context === undefined) {
    throw new Error('useOnlineStatus must be used within an OnlineStatusProvider');
  }
  return context;
};
